const API = process.env.REACT_APP_API_URL || 'http://localhost:3090';

async function api(path, options = {}) {
  const separator = path.includes('?') ? '&' : '?';
  const cacheBust = `${separator}_t=${Date.now()}`;
  const res = await fetch(`${API}${path}${cacheBust}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    if (res.status === 401) {
      window.location.href = `${API}/auth/login`;
      return new Promise(() => {});
    }
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw { status: res.status, ...error };
  }
  if (res.status === 204) return {};
  return res.json();
}

function toQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** URL SSE-потоку подій (EventSource сам додає cookie при withCredentials). */
export const telephonyEventsUrl = () => `${API}/telephony/events`;

// ── Стан PBX ──
export const getTelephonyStatus = () => api('/telephony/status');

// ── Лінії ──
export const getPbxLines = (includeInactive = false) => api(`/telephony/lines${toQuery({ includeInactive })}`);
export const createPbxLine = (data) => api('/telephony/lines', { method: 'POST', body: JSON.stringify(data) });
export const updatePbxLine = (id, data) => api(`/telephony/lines/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const syncPbxLines = () => api('/telephony/lines/sync', { method: 'POST' });
export const getPbxLineTypes = (includeInactive = false) => api(`/telephony/line-types${toQuery({ includeInactive })}`);
export const createPbxLineType = (data) => api('/telephony/line-types', { method: 'POST', body: JSON.stringify(data) });
export const updatePbxLineType = (id, data) => api(`/telephony/line-types/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

// ── Дзвінки ──
export const getCalls = (params) => api(`/telephony/calls${toQuery(params)}`);
export const getCall = (id) => api(`/telephony/calls/${id}`);
// Реакція на пропущений: ручна відмітка «опрацьовано» / зняття
export const markCallHandled = (id, note) => api(`/telephony/calls/${id}/handled`, { method: 'POST', body: JSON.stringify(note ? { note } : {}) });
export const clearCallHandled = (id) => api(`/telephony/calls/${id}/handled`, { method: 'DELETE' });
// Відмітка «помилковий дзвінок» (будь-який статус): reason = SPAM | WRONG_NUMBER | NO_ACTION_REQUIRED | OTHER
export const markCallFalse = (id, reason, note) => api(`/telephony/calls/${id}/false`, { method: 'POST', body: JSON.stringify(note ? { reason, note } : { reason }) });
export const clearCallFalse = (id) => api(`/telephony/calls/${id}/false`, { method: 'DELETE' });
/** URL аудіо запису для <audio> (cookie-сесія, тому crossOrigin="use-credentials"). */
export const callRecordingUrl = (id) => `${API}/telephony/calls/${id}/recording`;
export const lookupNumber = (number) => api(`/telephony/calls/lookup${toQuery({ number })}`);
export const searchCallNumberPbxCalls = (number, force) => api(`/telephony/calls/pbx-search${toQuery({ number, force: force || undefined })}`);
export const rememberCallNumberPbxCall = (number, linkedId) => api('/telephony/calls/pbx-search/remember', { method: 'POST', body: JSON.stringify({ number, linkedId }) });

// ── Звернення ──
export const getInquiries = (params) => api(`/telephony/inquiries${toQuery(params)}`);
export const getInquiry = (id) => api(`/telephony/inquiries/${id}`);
/** Вивантаження звернень у .xlsx (з фільтрів списку враховуються «Канал» і період from/to): { blob, filename }. Лише оператор і адміністратор. */
export async function downloadInquiriesExcel(advertising = '', period = {}) {
  const res = await fetch(`${API}/telephony/inquiries/export${toQuery({ advertising, from: period.from, to: period.to })}`, { credentials: 'include' });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw { status: res.status, ...error };
  }
  const match = (res.headers.get('Content-Disposition') || '').match(/filename\*=UTF-8''([^;]+)/);
  return { blob: await res.blob(), filename: match ? decodeURIComponent(match[1]) : 'zvernennia.xlsx' };
}
/** Усі звернення рядками комірок без шапки (для копіювання в буфер): { rows }. Лише оператор і адміністратор. */
export const getInquiriesExcelRows = (advertising = '', period = {}) => api(`/telephony/inquiries/export-rows${toQuery({ advertising, from: period.from, to: period.to })}`);
/** Рядок звернення для Excel-журналу: { cells, seenUpTo, observedChangeAt }. */
export const getInquiryExcelRow = (id) => api(`/telephony/inquiries/${id}/excel-row`);

// ── Журнал змін звернень ──
export const getInquiryChanges = (params) => api(`/telephony/inquiry-changes${toQuery(params)}`);
export const getInquiryChangeEvents = (id) => api(`/telephony/inquiries/${id}/changes`);
export const markInquirySnapshotRead = (id, seenUpTo, observedChangeAt) => api(`/telephony/inquiries/${id}/snapshot-read`, { method: 'POST', body: JSON.stringify({ seenUpTo, observedChangeAt }) });
export const markInquirySnapshotUnread = (id) => api(`/telephony/inquiries/${id}/snapshot-unread`, { method: 'POST' });
export const getInquirySources = () => api('/telephony/inquiry-sources');
export const getInquiryReportByTopic = (params) => api(`/telephony/reports/inquiries-by-topic${toQuery(params)}`);
export const updateInquirySource = (source, isAdvertising) => api(`/telephony/inquiry-sources/${source}`, { method: 'PATCH', body: JSON.stringify({ isAdvertising }) });

export const createInquiry = (data) => api('/telephony/inquiries', { method: 'POST', body: JSON.stringify(data) });
export const uploadInquiryDraftAttachment = async (file, inquiryId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  const route = inquiryId
    ? `/telephony/inquiries/${encodeURIComponent(inquiryId)}/draft-attachments`
    : '/telephony/inquiries/draft-attachments';
  const res = await fetch(`${API}${route}?_t=${Date.now()}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw { status: res.status, ...error };
  }
  return res.json();
};
export const deleteInquiryDraftAttachment = (fileId, inquiryId = null) => {
  const route = inquiryId
    ? `/telephony/inquiries/${encodeURIComponent(inquiryId)}/draft-attachments/${encodeURIComponent(fileId)}`
    : `/telephony/inquiries/draft-attachments/${encodeURIComponent(fileId)}`;
  return api(route, { method: 'DELETE' });
};
export const downloadInquiryAttachmentUrl = (id, attachmentId) =>
  `${API}/telephony/inquiries/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/download`;
export const previewInquiryAttachmentUrl = (id, attachmentId) =>
  `${API}/telephony/inquiries/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/preview`;
export const deleteInquiryAttachment = (id, attachmentId) =>
  api(`/telephony/inquiries/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' });
export const updateInquiry = (id, data) => api(`/telephony/inquiries/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const claimInquiry = (id) => api(`/telephony/inquiries/${id}/claim`, { method: 'POST' });
// «Пінок»: вимагати реакцію менеджера (доручення на існуюче звернення) / зняти позначку вручну
export const raiseInquiryAttention = (id, note) => api(`/telephony/inquiries/${id}/attention`, { method: 'POST', body: JSON.stringify({ note }) });
// Відмітити дзвінок-першопричину пінка з картки звернення: { outcome: 'handled'|'false', reason?, note? } → { deleted }
export const reactToAttentionCall = (id, body) => api(`/telephony/inquiries/${id}/attention/call-reaction`, { method: 'POST', body: JSON.stringify(body) });
export const resolveInquiryAttention = (id) => api(`/telephony/inquiries/${id}/attention/resolve`, { method: 'POST' });
// «Пінок» з пропущеного дзвінка: сервер сам знаходить відкрите звернення клієнта або створює нове → { inquiryId, number, created }
export const raiseCallAttention = (callId, body = {}) => api(`/telephony/calls/${callId}/attention`, { method: 'POST', body: JSON.stringify(body) });
/** Видалити порожнє звернення (створене помилково); 409 — має історію, закривати подією «Помилково створено» */
export const deleteInquiry = (id) => api(`/telephony/inquiries/${id}`, { method: 'DELETE' });
export const assignInquiry = (id, data) => api(`/telephony/inquiries/${id}/assign`, { method: 'POST', body: JSON.stringify(data) });
export const setInquiryControl = (id, enabled, comment) => api(`/telephony/inquiries/${id}/control`, { method: 'POST', body: JSON.stringify({ enabled, comment }) });
export const setInquiryShipmentControl = (id, enabled, comment) => api(`/telephony/inquiries/${id}/shipment-control`, { method: 'POST', body: JSON.stringify({ enabled, comment }) });
export const confirmInquiryShipment = (id, comment) => api(`/telephony/inquiries/${id}/shipment-confirm`, { method: 'POST', body: JSON.stringify({ comment: comment?.trim() || null }) });
export const returnInquiryToQueue = (id, data) => api(`/telephony/inquiries/${id}/return-to-queue`, { method: 'POST', body: JSON.stringify(data) });
export const getInquirySuggestedCalls = (id) => api(`/telephony/inquiries/${id}/suggested-calls`);
export const searchInquiryPbxCalls = (id, force) => api(`/telephony/inquiries/${id}/pbx-search${toQuery({ force: force || undefined })}`);
export const rememberInquiryPbxCall = (id, linkedId) => api(`/telephony/inquiries/${id}/pbx-search/remember`, { method: 'POST', body: JSON.stringify({ linkedId }) });
export const saveInquiryPbxCall = (id, linkedId) => api(`/telephony/inquiries/${id}/pbx-search/save`, { method: 'POST', body: JSON.stringify({ linkedId }) });
export const getInquiryNumberHistory = (id) => api(`/telephony/inquiries/${id}/number-history`);
/** Зведена хронологія за телефоном/email до створення звернення: { items, count }. */
export const getNumberTimeline = ({ phone, email } = {}) => api(`/telephony/number-history${toQuery({ phone, email })}`);
export const linkInquiryCall = (id, callId) => api(`/telephony/inquiries/${id}/calls`, { method: 'POST', body: JSON.stringify({ callId }) });
export const unlinkInquiryCall = (id, callId) => api(`/telephony/inquiries/${id}/calls/${callId}`, { method: 'DELETE' });
export const getPendingDecisions = () => api('/telephony/inquiries/pending-decisions');

// ── Стрічка подій ──
export const getInquiryEvents = (id) => api(`/telephony/inquiries/${id}/events`);
export const addInquiryEvent = (id, data) => api(`/telephony/inquiries/${id}/events`, { method: 'POST', body: JSON.stringify(data) });
export const decideInquiryEvent = (id, eventId, data) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/decision`, { method: 'POST', body: JSON.stringify(data) });
export const updateInquiryEventInvoice = (id, eventId, data) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/invoice`, { method: 'PATCH', body: JSON.stringify(data) });
/** «Відв'язати рахунок»: видаляє подію рахунку та пов'язані автоподії 1С. */
export const unlinkInquiryEventInvoice = (id, eventId) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/invoice`, { method: 'DELETE' });
/** Клієнт відмовився від рахунку (причина обов'язкова): рахунок лишається в журналі, але не блокує завершення угоди. */
export const declineInquiryEventInvoice = (id, eventId, reason) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/invoice-decline`, { method: 'POST', body: JSON.stringify({ reason }) });
/** Зняти позначку «клієнт відмовився» — рахунок знову в розрахунку. */
export const restoreInquiryEventInvoice = (id, eventId) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/invoice-restore`, { method: 'POST' });
/** «Оновити з 1С»: сервер перечитує рахунок, оновлює знімок і створює події ONEC_* → OnecInvoiceSyncResponse. */
export const syncOnecInvoice = (id, eventId) => api(`/telephony/inquiries/${id}/events/${eventId}/invoice-sync`, { method: 'POST' });
/** «Завершити як угоду» — лише при повній оплаті та повному відвантаженні (сервер перевіряє знімок). */
export const finalizeInquiryDeal = (id, comment) =>
  api(`/telephony/inquiries/${id}/finalize-deal`, { method: 'POST', body: JSON.stringify({ comment: comment?.trim() || null }) });
export const uploadInquiryEventAttachment = async (id, eventId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API}/telephony/inquiries/${id}/events/${eventId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw { status: res.status, ...error };
  }
  return res.json();
};
export const deleteInquiryEventAttachment = (id, eventId, attachmentId) =>
  api(`/telephony/inquiries/${id}/events/${eventId}/attachments/${attachmentId}`, { method: 'DELETE' });

// ── Довідники звернень ──
// Рахунки з 1С (Документ.ЗаказПокупателя) — для події «Виставлено рахунок»
export const lookupOnecInvoices = (number) => api(`/onec/invoices${toQuery({ number })}`);
export const getOnecInvoice = (id) => api(`/onec/invoices/${encodeURIComponent(id)}`);
// Ручний пошук контактних осіб 1С за номером телефону + звірка контрагентів з Новою Поштою
export const lookupOnecContactPersons = (phone) => api(`/onec/contact-persons/lookup${toQuery({ phone })}`);
// Ручна перевірка контрагента в НП за ЄДРПОУ (+ опційно телефон) — усі контактні особи НП
export const npCheckCounterpartyByEdrpou = (edrpou, phone) => api(`/onec/contact-persons/np-check${toQuery({ edrpou, phone })}`);

export const getInquiryEventTypes = (params) => api(`/telephony/event-types${toQuery(params)}`);
export const createInquiryEventType = (data) => api('/telephony/event-types', { method: 'POST', body: JSON.stringify(data) });
export const updateInquiryEventType = (id, data) => api(`/telephony/event-types/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const getInquiryTopics = (includeInactive = false) => api(`/telephony/topics${toQuery({ includeInactive })}`);
/** Ліміти контролю для підказок у формі: { assignmentMinutes, reactionMinutes, shipmentHours, shipmentHoursBySource } */
export const getInquiryControlLimits = () => api('/telephony/inquiry-control-limits');
export const createInquiryTopic = (data) => api('/telephony/topics', { method: 'POST', body: JSON.stringify(data) });
export const updateInquiryTopic = (id, data) => api(`/telephony/topics/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

// ── Налаштування ──
export const getExtensionBindings = () => api('/telephony/users/extensions');

// ── Менеджери для призначення (основне місце роботи в підрозділі) ──
export const getManagers = (departmentId) => api(`/telephony/managers${toQuery({ departmentId })}`);
// Колеги для панелі у формі звернення: менеджери підрозділу + куратори напрямку
export const getColleagues = ({ departmentId, topicId } = {}) => api(`/telephony/colleagues${toQuery({ departmentId, topicId })}`);
export const getTelephonySettings = () => api('/telephony/settings');
/** Частковий PUT: `{ isEnabled }` або поля сверки з CDR `{ cdrSyncEnabled, cdrSyncMinutes, … }`. */
export const updateTelephonySettings = (patch) =>
  api('/telephony/settings', { method: 'PUT', body: JSON.stringify(typeof patch === 'boolean' ? { isEnabled: patch } : patch) });
/** Без range — плановий прохід (200 з підсумком); з `{ from, to }` — довантаження за період у фоні (202). */
export const runCdrSync = (range) =>
  api('/telephony/cdr-sync/run', { method: 'POST', body: JSON.stringify(range || {}) });

// ── prom.ua: замовлення (view_prom_orders) і синк (manage_prom_settings) ──
export const getInvoiceHints = (params) => api(`/telephony/invoice-hints${toQuery(params)}`);
export const getInvoiceHintsCount = () => api('/telephony/invoice-hints/count');
export const linkInvoiceHint = (id) => api(`/telephony/invoice-hints/${id}/link`, { method: 'POST' });
export const getInvoiceHintInvoice = (id) => api(`/telephony/invoice-hints/${id}/invoice`);
export const attachInvoiceHint = (id, inquiryId) => api(`/telephony/invoice-hints/${id}/attach`, { method: 'POST', body: JSON.stringify({ inquiryId }) });
export const dismissInvoiceHint = (id) => api(`/telephony/invoice-hints/${id}/dismiss`, { method: 'POST' });
export const getPromOrders = (params) => api(`/telephony/prom-orders${toQuery(params)}`);
export const getPromOrderBadges = () => api('/telephony/prom-orders/badges');
/** Перше відкриття картки знімає «непрочитано» на бекенді — просимо меню перечитати лічильники. */
const notifyBadgesChanged = (data) => {
  try { window.dispatchEvent(new CustomEvent('telephony.badges-changed')); } catch { /* ignore */ }
  return data;
};

export const getPromOrder = (id) => api(`/telephony/prom-orders/${id}`).then(notifyBadgesChanged);
export const linkPromOrder = (id, inquiryId) => api(`/telephony/prom-orders/${id}/link`, { method: 'POST', body: JSON.stringify({ inquiryId }) });
export const unlinkPromOrder = (id) => api(`/telephony/prom-orders/${id}/unlink`, { method: 'POST' });
export const setPromOrderContactPerson = (id, contactPersonId) => api(`/telephony/prom-orders/${id}/contact-person`, { method: 'POST', body: JSON.stringify({ contactPersonId }) });
export const markPromOrderHandled = (id, note) => api(`/telephony/prom-orders/${id}/handled`, { method: 'POST', body: JSON.stringify({ note: note || null }) });
export const clearPromOrderHandled = (id) => api(`/telephony/prom-orders/${id}/handled`, { method: 'DELETE' });
export const getPromSettings = () => api('/telephony/prom-settings');
export const updatePromSettings = (data) => api('/telephony/prom-settings', { method: 'PUT', body: JSON.stringify(data) });
export const runPromSyncNow = () => api('/telephony/prom-settings/sync-now', { method: 'POST' });

// ── Заявки з сайту (view_site_leads) і токени інтеграції (manage_site_leads) ──
export const getSiteLeads = (params) => api(`/telephony/site-leads${toQuery(params)}`);
export const getSiteLeadBadges = () => api('/telephony/site-leads/badges');
export const getSiteLeadForms = () => api('/telephony/site-leads/forms');
export const getSiteLeadSites = () => api('/telephony/site-leads/sites');
export const getSiteLeadCountries = () => api('/telephony/site-leads/countries');
export const getSiteLead = (id) => api(`/telephony/site-leads/${id}`).then(notifyBadgesChanged);
export const linkSiteLead = (id, inquiryId) => api(`/telephony/site-leads/${id}/link`, { method: 'POST', body: JSON.stringify({ inquiryId }) });
export const unlinkSiteLead = (id) => api(`/telephony/site-leads/${id}/unlink`, { method: 'POST' });
export const setSiteLeadContactPerson = (id, contactPersonId) => api(`/telephony/site-leads/${id}/contact-person`, { method: 'POST', body: JSON.stringify({ contactPersonId }) });
export const markSiteLeadHandled = (id, note) => api(`/telephony/site-leads/${id}/handled`, { method: 'POST', body: JSON.stringify({ note: note || null }) });
export const clearSiteLeadHandled = (id) => api(`/telephony/site-leads/${id}/handled`, { method: 'DELETE' });
export const deleteSiteLead = (id) => api(`/telephony/site-leads/${id}`, { method: 'DELETE' });
export const markSiteLeadSpam = (id) => api(`/telephony/site-leads/${id}/spam`, { method: 'POST' });
export const clearSiteLeadSpam = (id) => api(`/telephony/site-leads/${id}/spam`, { method: 'DELETE' });
export const getSiteLeadTokens = () => api('/telephony/site-lead-tokens');
export const createSiteLeadToken = (name, inquirySource) => api('/telephony/site-lead-tokens', { method: 'POST', body: JSON.stringify({ name, ...(inquirySource ? { inquirySource } : {}) }) });
export const revokeSiteLeadToken = (id) => api(`/telephony/site-lead-tokens/${id}`, { method: 'DELETE' });

// ── Імпорт Excel-журналу лідів (статистика) ──
/** Опитування фонової задачі: стадія й лічильник приходять із сервера, поки вона не завершиться. */
async function pollLeadLogJob(jobId, onProgress) {
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  for (;;) {
    let job;
    try {
      job = await api(`/telephony/lead-log/jobs/${jobId}`);
    } catch (e) {
      if (e?.status === 404) throw { status: 404, message: 'Задачу втрачено (сервер міг перезапуститися) — завантажте файл ще раз' };
      await wait(1500); // короткий збій мережі — пробуємо ще
      continue;
    }
    if (job.status === 'done') return job.result;
    if (job.status === 'error') throw { status: job.error?.status || 500, message: job.error?.message || 'Помилка імпорту' };
    onProgress?.({ phase: 'server', label: job.label, percent: job.percent, elapsedMs: job.elapsedMs });
    await wait(500);
  }
}

/**
 * Завантаження файлу: XHR (прогрес відправки), потім сервер запускає фонову задачу, а ми опитуємо її стан.
 * onProgress({ phase: 'upload', percent }) → onProgress({ phase: 'server', label, percent|null, elapsedMs }).
 */
function uploadLeadLog(path, file, overwriteFinal, onProgress) {
  const body = new FormData();
  body.append('file', file);
  body.append('overwriteFinal', overwriteFinal ? 'true' : 'false');
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}${path}`);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.({ phase: 'upload', percent: Math.round((e.loaded / e.total) * 100) });
    };
    xhr.upload.onload = () => onProgress?.({ phase: 'server', label: 'Файл відправлено, сервер починає обробку…', percent: null });
    xhr.onerror = () => reject({ status: 0, message: 'Немає зв’язку з сервером' });
    xhr.onload = () => {
      if (xhr.status === 401) { window.location.href = `${API}/auth/login`; return; }
      if (xhr.status < 200 || xhr.status >= 300) { reject({ status: xhr.status, message: xhr.statusText, ...(xhr.response || {}) }); return; }
      const jobId = xhr.response?.jobId;
      if (!jobId) { reject({ status: 500, message: 'Сервер не повернув задачу' }); return; }
      pollLeadLogJob(jobId, onProgress).then(resolve, reject);
    };
    xhr.send(body); // Content-Type виставить браузер (boundary)
  });
}
export const getLeadLogInfo = () => api('/telephony/lead-log');
export const getLeadLogSummary = () => api('/telephony/lead-log/summary');
export const getLeadLogEntries = (params) => api(`/telephony/lead-log/entries${toQuery(params)}`);
export const previewLeadLog = (file, overwriteFinal, onProgress) => uploadLeadLog('/telephony/lead-log/preview', file, overwriteFinal, onProgress);
export const importLeadLog = (file, overwriteFinal, onProgress) => uploadLeadLog('/telephony/lead-log/import', file, overwriteFinal, onProgress);
export const saveLeadLogAlias = (data) => api('/telephony/lead-log/aliases', { method: 'POST', body: JSON.stringify(data) });
export const deleteLeadLogAlias = (id) => api(`/telephony/lead-log/aliases/${id}`, { method: 'DELETE' });
export const createLeadLogUser = (data) => api('/telephony/lead-log/users', { method: 'POST', body: JSON.stringify(data) });
export const clearLeadLog =(data) => api('/telephony/lead-log/clear', { method: 'POST', body: JSON.stringify(data) });
